import React from "react";
import { Link } from "react-router-dom";
import { Reveal, LineReveal } from "@/lib/motion";

/** Interior page header (light) with top padding to clear the fixed nav. */
export function PageHeader({ eyebrow, title, intro, testid }) {
  const lines = Array.isArray(title) ? title : [title];
  return (
    <header className="aria-container pt-40 pb-16 md:pt-52 md:pb-24" data-testid={testid}>
      {eyebrow && (
        <Reveal>
          <div className="eyebrow mb-8">{eyebrow}</div>
        </Reveal>
      )}
      <h1 className="font-display text-5xl md:text-7xl lg:text-[6.5rem] leading-[0.95] tracking-tight text-aria-ink">
        <LineReveal lines={lines} animateOnLoad />
      </h1>
      {intro && (
        <Reveal delay={0.3}>
          <p className="mt-10 max-w-2xl text-xl md:text-2xl leading-relaxed text-aria-cocoa">
            {intro}
          </p>
        </Reveal>
      )}
    </header>
  );
}

/** Full-width dark call-to-action band. */
export function CTASection({
  eyebrow = "The Invitation",
  title = ["A limited number of", "engagements each season."],
  body = "Tell us about your occasion. We will respond personally to begin composing your experience.",
  to = "/book",
  cta = "Book ARIA",
}) {
  const lines = Array.isArray(title) ? title : [title];
  return (
    <section className="bg-aria-ink text-aria-ivory" data-testid="cta-section">
      <div className="aria-container py-28 md:py-40">
        <Reveal>
          <div className="eyebrow mb-8">{eyebrow}</div>
        </Reveal>
        <h2 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-4xl">
          <LineReveal lines={lines} animateOnLoad={false} />
        </h2>
        <Reveal delay={0.2}>
          <p className="mt-10 max-w-xl font-light text-lg text-aria-ivory/60 leading-relaxed">{body}</p>
          <Link
            to={to}
            data-testid="cta-book-link"
            className="mt-12 inline-flex items-center gap-4 border-b border-aria-brass pb-2 font-display text-3xl md:text-4xl text-aria-ivory transition-colors hover:text-aria-brass"
          >
            {cta}
            <span aria-hidden>→</span>
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

/** Section eyebrow + heading used across editorial sections. */
export function SectionTitle({ eyebrow, title, className = "", dark = false }) {
  const lines = Array.isArray(title) ? title : [title];
  return (
    <div className={className}>
      {eyebrow && <div className="eyebrow mb-6">{eyebrow}</div>}
      <h2
        className={`font-display text-4xl md:text-5xl lg:text-6xl leading-[1.03] tracking-tight ${
          dark ? "text-aria-ivory" : "text-aria-ink"
        }`}
      >
        <LineReveal lines={lines} animateOnLoad={false} />
      </h2>
    </div>
  );
}
